import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { professionalsAPI, appointmentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import KYCAlertBanner from '@/components/KYCAlertBanner';
import {
  Calendar,
  Clock,
  Users,
  Wallet,
  Video,
  Home,
  MapPin,
  Check,
  X,
  AlertCircle,
  Stethoscope,
  ArrowRight,
  Loader2,
  MessageCircle,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';

const ProfessionalDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, statsRes, appointmentsRes] = await Promise.all([
        professionalsAPI.getMyProfile(),
        professionalsAPI.getStats(),
        appointmentsAPI.getAll(),
      ]);
      
      setProfile(profileRes.data.profile);
      setStats(statsRes.data);
      setAppointments(appointmentsRes.data.appointments.slice(0, 5));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentAction = async (appointmentId, action) => {
    try {
      await appointmentsAPI.updateStatus(appointmentId, action);
      toast.success(action === 'confirmed' ? 'Rendez-vous accepté' : 'Rendez-vous refusé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'confirmed': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video_call': return Video;
      case 'home_visit': return Home;
      default: return MapPin;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  // If no profile exists, prompt to create one
  if (!profile) {
    return (
      <div className="section-padding py-6" data-testid="professional-dashboard">
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Complétez votre profil
          </h1>
          <p className="text-slate-600 mb-6">
            Pour commencer à recevoir des rendez-vous, vous devez d&apos;abord configurer votre profil professionnel.
          </p>
          <Button
            onClick={() => navigate('/pro/setup')}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full"
          >
            Configurer mon profil
          </Button>
        </div>
      </div>
    );
  }

  // If profile exists but not validated
  if (!profile.is_validated) {
    return (
      <div className="section-padding py-6" data-testid="professional-dashboard">
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Profil en attente de validation
          </h1>
          <p className="text-slate-600 mb-6">
            Votre profil est en cours de vérification par notre équipe. Vous recevrez une notification une fois validé.
          </p>
          <Badge className="bg-amber-100 text-amber-800">
            En attente de validation
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="professional-dashboard">
      {/* KYC Alert Banner */}
      <KYCAlertBanner userRole="professional" />
      
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Bonjour, Dr. {user?.full_name?.split(' ')[0]} 👋
          </h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Voici un aperçu de votre activité</p>
        </div>
        <Link to="/pro/profile" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white rounded-full text-sm px-4 py-2" data-testid="manage-availability-btn">
            <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="whitespace-nowrap">Disponibilités</span>
          </Button>
        </Link>
      </div>

      {/* Quick Actions Banner */}
      {(!profile.consultation_slots || Object.values(profile.consultation_slots || {}).every(slots => !slots || slots.length === 0)) && (
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4 px-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start sm:items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-amber-900 text-sm sm:text-base">Configurez vos disponibilités</h3>
                  <p className="text-xs sm:text-sm text-amber-700">Les patients ne peuvent pas encore réserver</p>
                </div>
              </div>
              <Link to="/pro/profile?tab=availability" className="w-full sm:w-auto">
                <Button variant="outline" size="sm" className="w-full sm:w-auto border-amber-500 text-amber-700 hover:bg-amber-100">
                  Configurer
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.appointments_today || 0}</p>
                <p className="text-xs text-slate-500">RDV aujourd&apos;hui</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.pending_appointments || 0}</p>
                <p className="text-xs text-slate-500">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.completed_appointments || 0}</p>
                <p className="text-xs text-slate-500">Consultations</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_earnings?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">FCFA gagnés</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/chat">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer bg-gradient-to-br from-[#E53935] to-[#D32F2F] h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="text-white min-w-0">
                <p className="font-semibold text-sm sm:text-base">Messages</p>
                <p className="text-xs text-white/80 truncate">Discutez avec vos patients</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/pro/home-care">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer bg-gradient-to-br from-[#FF9800] to-[#F57C00] h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div className="text-white min-w-0">
                <p className="font-semibold text-sm sm:text-base">Soins à domicile</p>
                <p className="text-xs text-white/80 truncate">Missions en attente</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/pro/credits">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-white" />
              </div>
              <div className="text-white min-w-0">
                <p className="font-semibold text-sm sm:text-base">Crédits</p>
                <p className="text-xs text-white/80 truncate">Gérez votre abonnement</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Pending Appointments */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Rendez-vous à traiter
          </h2>
          <Link to="/pro/appointments" className="text-red-500 hover:text-red-600 text-sm font-medium flex items-center gap-1">
            Voir tout <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {appointments.filter(a => a.status === 'pending').length > 0 ? (
          <div className="space-y-3">
            {appointments.filter(a => a.status === 'pending').map((appointment) => {
              const TypeIcon = getTypeIcon(appointment.appointment_type);
              return (
                <Card key={appointment.id} className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{appointment.patient_name}</h3>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                              {appointment.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              {appointment.time_slot}
                            </span>
                            <span className="flex items-center gap-1">
                              <TypeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                            </span>
                          </div>
                          {appointment.notes && (
                            <p className="text-xs sm:text-sm text-slate-500 mt-2 line-clamp-1">{appointment.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 self-end sm:self-start flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAppointmentAction(appointment.id, 'cancelled')}
                          className="border-red-200 text-red-600 hover:bg-red-50 h-9 w-9 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAppointmentAction(appointment.id, 'confirmed')}
                          className="bg-red-500 hover:bg-red-600 h-9 w-9 p-0"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun rendez-vous en attente</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upcoming Appointments */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Prochains rendez-vous
          </h2>
        </div>

        {appointments.filter(a => a.status === 'confirmed').length > 0 ? (
          <div className="space-y-3">
            {appointments.filter(a => a.status === 'confirmed').map((appointment) => {
              const TypeIcon = getTypeIcon(appointment.appointment_type);
              const isVideoCall = appointment.appointment_type === 'video_call';
              return (
                <Card key={appointment.id} className="border-0 shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{appointment.patient_name}</h3>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                              {appointment.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              {appointment.time_slot}
                            </span>
                            <span className="flex items-center gap-1">
                              <TypeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                        {isVideoCall && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/video-room/${appointment.id}`)}
                            className="bg-[#1A237E] hover:bg-[#3949AB] text-white rounded-lg gap-1 text-xs sm:text-sm"
                            data-testid={`pro-join-video-btn-${appointment.id}`}
                          >
                            <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Démarrer</span>
                          </Button>
                        )}
                        <Badge className={`${getStatusColor(appointment.status)} text-xs`}>
                          Confirmé
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun rendez-vous confirmé</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfessionalDashboard;
